<template>
  <div :class="[getContainerClass, {'link-button__container--disabled': isDisabled}]">
    <component
      :is="elementName"
      :id="id"
      class="link-button"
      :class="[widthClass, themeClass]"
      v-bind="getNavAttribute">
      <slot>
        <span v-if="ctaText">{{ ctaText }}</span>
      </slot>
    </component>
  </div>
</template>

<script lang="ts">
  import { defineComponent, computed, PropType } from '@vue/composition-api';
  import {
    ButtonSizes, ButtonAlignment, ButtonThemes, LinkButtonElements, ButtonMode,
  } from './types';

  export const BUTTON_TEXT_SIZES: ButtonSizes = {
    'x-small': 'button-x-small',
    small: 'button-small',
    medium: 'button-medium',
    large: 'button-large',
    'x-large': 'button-x-large',
  };

  export const BUTTON_ALIGNMENT: ButtonAlignment = {
    left: 'link-button--left',
    center: '',
    right: 'link-button--right',
  };

  export default defineComponent({
    name: 'LinkButton',
    props: {
      id: {
        type: String,
        required: false,
        default: null,
      },
      ctaText: {
        type: String,
        default: '',
        required: false,
      },
      ctaLink: {
        type: String,
        required: true,
      },
      buttonTheme: {
        type: String as PropType<ButtonThemes>,
        default: 'button-primary',
      },
      buttonSize: {
        type: String,
        default: 'medium',
      },
      buttonAlignment: {
        type: String,
        default: 'left',
      },
      useHeadingFont: {
        type: Boolean,
        required: false,
        default: false,
      },
      fullWidth: {
        type: Boolean,
        default: false,
      },
      elementName: {
        type: String as PropType<LinkButtonElements>,
        default: 'a',
      },
      mobileAlignmentCenter: {
        type: Boolean,
        default: false,
      },
      disabled: {
        type: Boolean,
        default: false,
        required: false,
      },
      pressed: {
        type: Boolean,
        default: false,
        required: false,
      },
      mode: {
        type: String as PropType<ButtonMode>,
        default: 'light',
        required: false,
      },
    },
    setup(props) {
      const isDisabled = computed(() => props.disabled);
      const isLinkComponent = computed(() => props.elementName === 'NuxtLink' || props.elementName === 'router-link');

      const getNavAttribute = computed(() => {
        // TODO remove / from nuxtlink and router-link
        if (isLinkComponent.value) {
          if (props.ctaLink.includes('#')) {
            const [page, id] = props.ctaLink.split('#');
            return { to: { path: page, hash: `#${id}` } };
          }
          return { to: `/${props.ctaLink}` };
        }
        return { href: props.ctaLink };
      });

      const themeClass = computed(() => {
        // Text button has simple styling with no additional classes
        if (props.buttonTheme === 'button-text') {
          return props.buttonTheme;
        }

        // Build class list based on the button state and properties
        const classes = ['button-layout'];

        // Add theme
        classes.push(props.buttonTheme);

        // Add mode modifier if dark mode is specified
        if (props.mode === 'dark') {
          classes.push(`${props.buttonTheme}--on-dark`);
        }

        // Add mode modifier if dark-white mode is specified
        if (props.mode === 'dark-white') {
          classes.push(`${props.buttonTheme}--on-dark-white`);
        }

        // Add pressed modifier to the theme if pressed
        if (props.pressed && !isDisabled.value) {
          classes.push(`${props.buttonTheme}--pressed`);
        }

        // Add disabled modifier to the theme if disabled (takes precedence over pressed)
        if (isDisabled.value) {
          classes.push(`${props.buttonTheme}--disabled`, 'link-button--disabled');
        }

        // Add size class
        classes.push(BUTTON_TEXT_SIZES[props.buttonSize as keyof ButtonSizes]);

        // Add heading font if enabled
        if (props.useHeadingFont) {
          classes.push('fc-heading-5');
        }

        // Add mobile center alignment if enabled
        if (props.mobileAlignmentCenter) {
          classes.push('link-button--mobile-center');
        }

        return classes.join(' ');
      });

      return {
        isDisabled,
        getNavAttribute,
        getContainerClass: computed(() => (props.fullWidth ? 'fc-block' : 'fc-flex')),
        widthClass: computed(() => (props.fullWidth ? 'link-button--full-width' : (BUTTON_ALIGNMENT)[props.buttonAlignment as keyof ButtonAlignment])),
        themeClass,
      };
    },
  });
</script>

<style lang="scss" scoped>
  @import "../../styles/exports";

  .link-button {
    &__container--disabled {
      cursor: not-allowed;
    }

    &--full-width {
      width: 100%;
    }

    &--disabled {
      pointer-events: none;
    }

    &--left {
      margin: get-spacing-level(0) auto get-spacing-level(0) get-spacing-level(0);
    }

    &--right {
      margin: get-spacing-level(0) get-spacing-level(0) get-spacing-level(0) auto;
    }

    &--mobile-center {
      @media #{$mobile} {
        margin: auto;
      }
    }
  }

  .button-text {
    background: none;
    border: none;
    color: $color-blue;
    font-size: get-font-size-level(6);

    &:hover {
      text-decoration: underline;
    }
  }
</style>
